<script setup lang="ts">
import { computed, ref } from 'vue';
import Dialog from 'primevue/dialog';
import RadioButton from 'primevue/radiobutton';
import DatePicker from 'primevue/datepicker';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import Menu from 'primevue/menu';
import moment from 'moment';
import { ButtonType, CustomButton } from '@/components';
import { useExportStore } from '@/stores/export';
import { type PostExport, type IForm, type ICase, type ExportColumnHeader, type ExportOptions } from '@/models';
import { useFormStore } from '@/stores/forms';
import { useCaseStore } from '@/stores/case';
import { useI18n } from 'vue-i18n';
import { useStudyStore } from '@/stores/study';
import {
  PERIOD_TYPE,
  FORMAT_TYPE,
  CHOICE_TYPE,
  CHECKBOX_VALUE,
  MATRIX_HEADER,
  COLUMN_HEADERS,
  buildFormStateFromOptions,
  type MatrixHeader,
  type FormatType,
  type ChoiceType,
  type PeriodType
} from '@/utils/exportUtils';

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const { t: $t } = useI18n();
const formStore = useFormStore();
const exportStore = useExportStore();
const caseStore = useCaseStore();
const studyStore = useStudyStore();

const selectedForm = ref<IForm[]>([]);
const selectedCases = ref<ICase[] | null>(null);
const periodType = ref<PeriodType | undefined>();
const periodDates = ref<[Date | null, Date | null] | null>(null);
const formatType = ref<FormatType | undefined>();
const choiceType = ref<ChoiceType | undefined>();
const includeArchive = ref<string[] | undefined>();
const includeFiles = ref<string[] | undefined>();
const includeMetadata = ref<string[] | undefined>();
const separateMatrixSheets = ref(true);
const matrixHeaderColumns = ref<MatrixHeader>(MATRIX_HEADER.COLUMNS);
const selectedTimezone = ref<string>();
const localTimezoneForSystemDates = ref<boolean>(false);

//@ts-ignore shows error in the build process because of the type, but should work.
const timezones = Intl.supportedValuesOf('timeZone');

const columnHeaders = ref<ExportColumnHeader[]>(COLUMN_HEADERS);
const selectedColumnHeader = ref<ExportColumnHeader>(COLUMN_HEADERS[0]);

const showSuggestBanner = ref(false);

const applyOptions = (opts: ExportOptions) => {
  const state = buildFormStateFromOptions(
    opts,
    formStore.formList as IForm[],
    caseStore.studyCaseList as unknown as ICase[],
    columnHeaders.value
  );
  if (state.selectedForm !== undefined) selectedForm.value = state.selectedForm;
  if (state.selectedCases !== undefined) selectedCases.value = state.selectedCases;
  if (state.periodType !== undefined) periodType.value = state.periodType;
  if (state.periodDates !== undefined) periodDates.value = state.periodDates;
  if (state.formatType !== undefined) formatType.value = state.formatType;
  if (state.choiceType !== undefined) choiceType.value = state.choiceType;
  if (state.includeArchive !== undefined) includeArchive.value = state.includeArchive;
  if (state.includeFiles !== undefined) includeFiles.value = state.includeFiles;
  if (state.includeMetadata !== undefined) includeMetadata.value = state.includeMetadata;
  if (state.separateMatrixSheets !== undefined) separateMatrixSheets.value = state.separateMatrixSheets;
  if (state.matrixHeaderColumns !== undefined) matrixHeaderColumns.value = state.matrixHeaderColumns;
  if (state.localTimezoneForSystemDates !== undefined) localTimezoneForSystemDates.value = state.localTimezoneForSystemDates;
  if (state.selectedTimezone !== undefined) selectedTimezone.value = state.selectedTimezone;
  if (state.selectedColumnHeader !== undefined) selectedColumnHeader.value = state.selectedColumnHeader;
  showSuggestBanner.value = false;
};

const onDialogShow = () => {
  if (exportStore.exportList.length) {
    showSuggestBanner.value = true;
  }
};

const applySuggestedSettings = () => {
  if (exportStore.latestExportOptions) {
    applyOptions(exportStore.latestExportOptions);
  }
};

const historyMenu = ref();

const exportHistoryItems = computed(() => {
  return [...exportStore.exportList]
    .filter(e => e.options)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 15)
    .map(e => ({
      label: `${e.exportFormNames} — ${moment(e.createdAt).format('MMM D, YYYY')}`,
      command: () => applyOptions(e.options as ExportOptions)
    }));
});

const toggleHistoryMenu = (event: Event) => {
  historyMenu.value.toggle(event);
};

const emits = defineEmits(['onClose']);

const closeDialog = () => {
  emits('onClose');
  clearForm();
  showSuggestBanner.value = false;
};

const clearForm = () => {
  selectedForm.value = [];
  selectedCases.value = null;
  periodType.value = undefined;
  periodDates.value = null;
  formatType.value = undefined;
  choiceType.value = undefined;
  includeArchive.value = undefined;
  selectedColumnHeader.value = COLUMN_HEADERS[0];
  separateMatrixSheets.value = true;
  matrixHeaderColumns.value = MATRIX_HEADER.COLUMNS;
  localTimezoneForSystemDates.value = false;
  selectedTimezone.value = undefined;
};

const radioChangeListener = () => {
  if (periodType.value === PERIOD_TYPE.ALL) {
    periodDates.value = null;
  }
};

const createExport = async () => {
  const postExport: PostExport = {
    formIdentifiers: selectedForm.value.map(form => form.identifier),
    choiceLabels: choiceType.value === CHOICE_TYPE.LABELS,
    format: formatType.value ?? '',
    includeArchive: includeArchive.value ?
      includeArchive.value[0] === CHECKBOX_VALUE.ARCHIVE :
      false,
    files: includeFiles.value ? includeFiles.value[0] === CHECKBOX_VALUE.FILES : false,
    metadata: includeMetadata.value ?
      includeMetadata.value[0] === CHECKBOX_VALUE.METADATA :
      false,
    header: selectedColumnHeader.value.value,
    timezone: localTimezoneForSystemDates.value ? selectedTimezone.value : undefined,
    separateMatrixSheets: separateMatrixSheets.value,
    exportColumnHeadersForMatrix: separateMatrixSheets.value ? undefined : matrixHeaderColumns.value
  };
  if (selectedCases.value) {
    postExport.caseIdentifiers = selectedCases.value.map(
      caseItem => caseItem.identifier
    );
  }
  if (periodDates.value) {
    postExport.period = {
      start: moment
        .utc(moment(periodDates.value[0]).format('YYYY-MM-DD'))
        .startOf('day')
        .valueOf(),
      end: moment
        .utc(moment(periodDates.value[1]).format('YYYY-MM-DD'))
        .endOf('day')
        .valueOf()
    };
  }
  await exportStore.createStudyExport(postExport);
  closeDialog();
};

const checkPeriodDates = () => {
  const nullValues = periodDates.value ?
    periodDates.value.filter((date: Date | null) => date == null) :
    [];
  return periodDates.value && nullValues.length > 0;
};

const correctPeriodDates = computed(() => checkPeriodDates());

const enableExport = computed(
  () =>
    selectedForm.value.length > 0 &&
    periodType.value !== undefined &&
    (periodType.value === PERIOD_TYPE.SELECT ? !checkPeriodDates() : true) &&
    formatType.value !== undefined &&
    choiceType.value !== undefined
);
</script>

<template>
  <Dialog :visible="visible"
    modal
    header="Export"
    class="dialog"
    style="width: 700px"
    @update:visible="closeDialog"
    @show="onDialogShow">
    <span class="p-text-secondary block mb-5">Configure your export</span>
    <div v-if="showSuggestBanner" class="saved-settings-banner">
      <span>Apply settings from a previous export?</span>
      <div class="saved-settings-banner-actions">
        <CustomButton text="Apply latest"
          :type="ButtonType.OUTLINED"
          :icon="{ icon: 'check', style: 'fas' }"
          @onClick="applySuggestedSettings"/>
        <CustomButton text="Choose from history"
          :type="ButtonType.OUTLINED"
          :icon="{ icon: 'chevron-down', style: 'fas' }"
          @onClick="toggleHistoryMenu"/>
        <Menu ref="historyMenu"
          :model="exportHistoryItems"
          :popup="true"/>
        <CustomButton :type="ButtonType.ICON_ONLY_OUTLINED"
          :icon="{ icon: 'xmark', style: 'fas' }"
          class="p-0"
          @onClick="showSuggestBanner = false"/>
      </div>
    </div>
    <div class="group form">
      <div>
        Form
        <template v-if="!formStore.formList.length">
          (Forms not found)
        </template>
      </div>
      <div class="p-dropdown-wrapper">
        <MultiSelect v-model="selectedForm"
          placeholder="Select Forms"
          :options="formStore.formList"
          :disabled="!formStore.formList.length"
          filter
          optionLabel="title"/>
      </div>
    </div>
    <div class="group form">
      <div>Cases</div>
      <div class="p-dropdown-wrapper">
        <MultiSelect v-model="selectedCases"
          placeholder="Select cases"
          :options="caseStore.studyCaseList"
          filter
          optionLabel="label"/>
      </div>
    </div>
    <div class="group period">
      <div>Select period</div>
      <div class="radio-group">
        <div class="radio-button one">
          <RadioButton v-model="periodType"
            inputId="period1"
            name="all"
            @change="radioChangeListener"
            :value="PERIOD_TYPE.ALL"/>
          <label for="all" class="ml-2">All</label>
        </div>
        <div class="radio-button">
          <RadioButton v-model="periodType"
            inputId="period2"
            name="select"
            @change="radioChangeListener"
            :value="PERIOD_TYPE.SELECT"/>
          <label for="select" class="ml-2">Select Range</label>
        </div>
      </div>
      <DatePicker v-model="periodDates"
        selectionMode="range"
        :manualInput="false"
        :maxDate="moment().toDate()"
        showIcon
        :disabled="periodType !== PERIOD_TYPE.SELECT"/>
    </div>
    <div class="group format">
      <div>File format</div>
      <div class="radio-group">
        <div class="radio-button one">
          <RadioButton v-model="formatType"
            inputId="format1"
            name="csv"
            :value="FORMAT_TYPE.CSV"/>
          <label for="csv" class="ml-2">.csv (Ideal for large export)</label>
        </div>
        <div class="radio-button">
          <RadioButton v-model="formatType"
            inputId="format2"
            name="xlsx"
            :value="FORMAT_TYPE.XLSX"/>
          <label for="xlsx" class="ml-2">.xlsx (Excel)</label>
        </div>
      </div>
    </div>
    <div class="group choice">
      <div>Display of choice fields</div>
      <div class="radio-group">
        <div class="radio-button one">
          <RadioButton v-model="choiceType"
            inputId="choice1"
            name="coded"
            :value="CHOICE_TYPE.CODED"/>
          <label for="coded" class="ml-2">Coded values (eg. '1')</label>
        </div>
        <div class="radio-button">
          <RadioButton v-model="choiceType"
            inputId="choice2"
            name="labels"
            :value="CHOICE_TYPE.LABELS"/>
          <label for="labels" class="ml-2">Choice Labels (eg. 'female')</label>
        </div>
      </div>
    </div>
    <div class="group form" v-if="studyStore.selectedStudy?.flags?.customDataDictionary !== 'PUMP'">
      <div>Column Header</div>
      <div class="p-dropdown-wrapper">
        <Select v-model="selectedColumnHeader"
          placeholder="Select column header"
          :options="columnHeaders"
          optionLabel="label"/>
      </div>
    </div>
    <div class="archive-group-grid">
      <div class="group archive">
        <div class="archive-label">Include archived entries</div>
        <Checkbox v-model="includeArchive"
          inputId="archive"
          name="archive"
          :value="CHECKBOX_VALUE.ARCHIVE"/>
      </div>
      <div class="group archive">
        <div class="archive-label">Include uploaded files</div>
        <Checkbox v-model="includeFiles"
          inputId="files"
          name="files"
          :value="CHECKBOX_VALUE.FILES"/>
      </div>
      <div class="group archive">
        <div class="archive-label">Include entry metadata</div>
        <Checkbox v-model="includeMetadata"
          inputId="metadata"
          name="metadata"
          :value="CHECKBOX_VALUE.METADATA"/>
      </div>
    </div>
    <div class="group form" v-if="studyStore.selectedStudy?.flags?.customDataDictionary !== 'PUMP'">
      <div class="timezone-label">
        <div class="shifted-dates-label">Localize timezone</div>
        <FontAwesomeIcon :icon="[`fas`, `square-info`]"
          v-tooltip="$t('export-local-timezone')"
          class="shifted-dates-tooltip"/>

        <Checkbox v-model="localTimezoneForSystemDates"
          binary
          inputId="local-timezone-system-dates"
          name="local-timezone-system-dates"
          value="local-timezone-system-dates"/>
      </div>
      <div class="p-dropdown-wrapper" v-if="localTimezoneForSystemDates">
        <Select v-model="selectedTimezone"
          placeholder="Select timezone"
          filter
          :options="timezones"/>
      </div>
    </div>
    <div class="group form">
      <div class="group archive">
        <div class="archive-label">Separate sheets for Matrix fields</div>
        <Checkbox v-model="separateMatrixSheets"
          inputId="separate-matrix-sheets"
          name="separate-matrix-sheets"
          :binary="true"
          value="true"/>
      </div>
      <div class="group choice" v-if="!separateMatrixSheets">
      <div>Select column header type for matrix</div>
        <div class="radio-group">
          <div class="radio-button one">
            <RadioButton v-model="matrixHeaderColumns"
              inputId="columns"
              name="columns"
              :value="MATRIX_HEADER.COLUMNS"/>
            <label for="columns" class="ml-2">Columns</label>
          </div>
          <div class="radio-button">
            <RadioButton v-model="matrixHeaderColumns"
              inputId="rows"
              name="rows"
              :value="MATRIX_HEADER.ROWS"/>
            <label for="rows" class="ml-2">Rows</label>
          </div>
        </div>
      </div>
    </div>
    <div class="error">
      <Message severity="error" v-if="correctPeriodDates">
        Please select the correct date "range"
      </Message>
    </div>
    <div class="buttons">
      <CustomButton text="Cancel"
        :type="ButtonType.OUTLINED"
        @onClick="closeDialog"/>
      <CustomButton text="Create Export"
        :type="ButtonType.POSITIVE"
        :marginLeft="16"
        :disabled="!enableExport"
        :loading="exportStore.createExportLoader.isLoading"
        @onClick="createExport"/>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
@import "../../assets/variables.module";
.group {
  margin-bottom: 16px;
}
.group.form {
  margin-top: 16px;
}
:deep(.p-dropdown) {
  width: 100%;
}
.p-dropdown-wrapper {
  padding-top: 12px;
}
.p-select {
  width: 100%;
}
.period-group {
  margin-bottom: 16px;
}
.radio-group {
  display: flex;
  align-items: center;
  padding: 12px;
}
.radio-button {
  display: flex;
  align-items: center;
}
.radio-button.one {
  margin-right: 32px;
}
.radio-button label {
  margin-left: 8px;
}
:deep(.p-calendar) {
  padding-left: 12px;
  padding-bottom: 12px;

  > .p-button {
    background-color: $primary-color;
    border-color: $primary-color;
  }
}
:deep(.p-calendar:not(.p-calendar-disabled).p-focus > .p-inputtext) {
  outline-color: $primary-color;
}
:deep(.p-inputtext) {
  width: 220px;
}
.format-group {
  margin-bottom: 16px;
}
.radio-group.choice {
  flex-direction: column;
  align-items: flex-start;

  .radio-button.one {
    margin-bottom: 12px;
  }
}
.group.archive {
  display: flex;
  align-items: center;

  .archive-label {
    margin-right: 16px;
  }
}
.shifted-dates-label {
  margin-right: 4px;
}
.shifted-dates-tooltip {
  margin-right: 16px;
}
:deep(
    .p-checkbox:not(.p-disabled):has(.p-checkbox-input:checked).p-highlight
      .p-checkbox-box
  ) {
  background-color: $primary-color !important;
  border-color: $primary-color !important;
}
.buttons {
  display: flex;
  justify-content: flex-end;
}
.error {
  margin-bottom: 16px;
}
:deep(.p-message-text) {
  flex: 1;
  margin-left: 16px;
}
.p-multiselect {
  width: 100%;
}
.archive-group-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 24px;
  margin-bottom: 16px;
}
.timezone-label {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.saved-settings-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  color: #3817FF;
  margin-bottom: 8px;
  margin-top: 8px;
  background-color: #E9E9FF;
  border: 1px solid #3817FF;
  border-radius: 6px;
  gap: 12px;
}
.saved-settings-banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
